import { useCallback, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { DSButton, DSTypography } from '@eiv2/design-system';
import { PageHeader } from '../components/PageHeader';
import { RequestState } from '../components/RequestState';
import { OrganizerLeads } from '../components/remarketing/OrganizerLeads';
import { OrganizerTracking, type TrackingTab } from './OrganizerTracking';
import { RemarketingJourneys } from '../components/remarketing/RemarketingJourneys';
import type { ListJourneysParams } from '../api/client';
import type { OrganizerDataApi, OrganizerLeadParams } from './api';
import type {
  OrganizerAccess,
  OrganizerForbiddenReason,
  OrganizerSession,
  OrganizerSessionState
} from './session';

// Área do organizador aberta pelo Gestão (/organizer/remarketing). Não usa o
// AuthContext administrativo nem o layout do painel: identidade, organização e
// eventos vêm do `/api/organizer/access` da sessão. Cada época (identidade +
// organização) monta um espaço de trabalho novo — trocar de identidade descarta
// filtros, paginação, dados e consultas da anterior.

export interface OrganizerRemarketingPageProps {
  session: OrganizerSession;
  api: OrganizerDataApi;
}

export function OrganizerRemarketingPage({ session, api }: OrganizerRemarketingPageProps) {
  const state = useSyncExternalStore(session.subscribe, session.getState, session.getState);

  return (
    <main className="min-h-dvh bg-body-light-mode-1 px-4 py-4 md:px-8 md:py-8">
      <div className="mx-auto flex w-full max-w-[1320px] flex-col">
        {state.status === 'ready' ? (
          <OrganizerWorkspace access={state.access} api={api} key={state.epoch} />
        ) : (
          <SessionNotice onRetry={() => session.retry()} state={state} />
        )}
      </div>
    </main>
  );
}

const FORBIDDEN_MESSAGES: Record<OrganizerForbiddenReason, string> = {
  gestao_denied: 'O Remarketing não está liberado para a sua conta ou organização no Gestão.',
  no_contacts_permission:
    'Sua conta precisa da permissão de gerenciar contatos para ver jornadas e leads.',
  permission_denied:
    'Sua conta precisa da permissão de gerenciar contatos para ver jornadas e leads.',
  not_an_organizer: 'Sua conta não tem acesso de organizador a estes dados.',
  organization_not_authorized: 'Sua conta não tem acesso de organizador a estes dados.',
  forbidden: 'Sua conta não tem acesso de organizador a estes dados.'
};

function Notice({
  title,
  message,
  action
}: {
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="grid min-h-[calc(100dvh-4rem)] place-items-center">
      <div className="flex max-w-md flex-col items-center gap-2 text-center" role="status">
        <DSTypography as="h1" className="text-[18px] font-semibold text-dark-3">
          {title}
        </DSTypography>
        <DSTypography as="p" className="text-[14px] font-normal text-gray-5">
          {message}
        </DSTypography>
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
    </div>
  );
}

function SessionNotice({
  state,
  onRetry
}: {
  state: Exclude<OrganizerSessionState, { status: 'ready' }>;
  onRetry: () => void;
}) {
  switch (state.status) {
    case 'connecting':
      return (
        <RequestState
          title="Conectando ao Gestão"
          message="Conferindo sua sessão e seus acessos."
        />
      );
    case 'misconfigured':
      return (
        <Notice
          title="Área do organizador não configurada"
          message="Esta área ainda não foi habilitada neste ambiente. Avise a equipe técnica."
        />
      );
    case 'not_embedded':
      return (
        <Notice
          title="Abra o Remarketing pelo Gestão"
          message="Esta área funciona dentro do Gestão de Inscrições. Entre no Gestão e acesse o menu Remarketing."
        />
      );
    case 'session_ended':
      return (
        <Notice
          title="Sessão encerrada"
          message="Sua sessão no Gestão terminou. Recarregue a página do Gestão para continuar."
        />
      );
    case 'forbidden':
      return (
        <Notice
          title="Sem permissão para jornadas e leads"
          message={FORBIDDEN_MESSAGES[state.reason]}
        />
      );
    case 'unavailable':
      return (
        <Notice
          title="Remarketing indisponível"
          message="Não foi possível falar com o Gestão ou com o Remarketing agora. Nenhum dado foi alterado."
          action={
            <DSButton className="w-auto" onClick={onRetry} type="button" variant="secondary">
              Tentar novamente
            </DSButton>
          }
        />
      );
  }
}

type Tab = 'journeys' | 'leads' | TrackingTab;

function OrganizerWorkspace({ access, api }: { access: OrganizerAccess; api: OrganizerDataApi }) {
  const { organization } = access;
  const tabs = (
    [
      { id: 'journeys', label: 'Jornadas', operation: 'journeys:read' },
      { id: 'overview', label: 'Visão geral', operation: 'journeys:read' },
      { id: 'funnel', label: 'Funil', operation: 'journeys:read' },
      { id: 'checkouts', label: 'Carrinhos', operation: 'journeys:read' },
      { id: 'activities', label: 'Atividades', operation: 'journeys:read' },
      { id: 'leads', label: 'Leads', operation: 'leads:read' }
    ] as const
  ).filter((item) => organization.operations.includes(item.operation));
  const [tab, setTab] = useState<Tab>(tabs[0]?.id ?? 'journeys');
  const eventScope = useMemo(
    () => ({ events: organization.events, eventsTruncated: organization.eventsTruncated }),
    [organization.events, organization.eventsTruncated]
  );

  const listJourneys = useCallback(
    (params: ListJourneysParams, signal?: AbortSignal) =>
      api.listJourneys(organization.id, params, signal),
    [api, organization.id]
  );
  const listLeads = useCallback(
    (params: OrganizerLeadParams, signal?: AbortSignal) =>
      api.listLeads(organization.id, params, signal),
    [api, organization.id]
  );

  const header = (
    <PageHeader
      title="Checkout e remarketing"
      subtitle={`Acompanhe as inscrições e os contatos dos seus eventos${organization.name ? ` em ${organization.name}` : ''}.`}
    />
  );

  // Sem evento vinculado não há o que consultar — e nada de busca "geral".
  if (organization.events.length === 0 && !organization.eventsTruncated) {
    return (
      <div>
        {header}
        <Notice
          title="Nenhum evento vinculado"
          message="Sua conta ainda não está vinculada a eventos desta organização. Peça acesso ao responsável pela organização no Gestão."
        />
      </div>
    );
  }

  return (
    <div>
      {header}

      <div
        aria-label="Checkout e remarketing"
        className="mb-6 flex flex-wrap gap-1 border-b border-gray-3"
        role="tablist"
      >
        {tabs.map((item) => {
          const selected = tab === item.id;
          return (
            <button
              aria-controls={`organizer-panel-${item.id}`}
              aria-selected={selected}
              className={[
                'cursor-pointer border-b-2 px-4 py-2.5 text-[14px] font-semibold transition-colors duration-150',
                selected
                  ? 'border-brand-primary-3 text-brand-primary-3'
                  : 'border-transparent text-gray-5 hover:text-dark-3'
              ].join(' ')}
              id={`organizer-tab-${item.id}`}
              key={item.id}
              onClick={() => setTab(item.id)}
              role="tab"
              type="button"
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div aria-labelledby={`organizer-tab-${tab}`} id={`organizer-panel-${tab}`} role="tabpanel">
        {tab === 'journeys' ? (
          <RemarketingJourneys eventScope={eventScope} listJourneys={listJourneys} />
        ) : tab === 'leads' ? (
          <OrganizerLeads eventScope={eventScope} listLeads={listLeads} />
        ) : (
          <OrganizerTracking access={access} api={api} tab={tab} />
        )}
      </div>
    </div>
  );
}
